/* Mar Soreli — fast swatch swap (Sections API + server-side variant preselect + delegated binding) */

(() => {
  // ===== Config (tweak to taste) =====
  const PREFETCH_ON_HOVER = true;     // prefetch JSON + section when you hover a swatch
  const PREFETCH_ON_IDLE  = false;    // background prefetch for all swatches when the browser is idle
  const PREFETCH_IN_VIEW  = true;     // prefetch as swatches enter viewport
  const PREFETCH_LIMIT    = 10;       // cap number of handles to prefetch in background
  const CONCURRENCY       = 4;        // prefetch concurrency for idle/in-view

  // ===== Tiny utils =====
  const $  = (s, r=document) => r.querySelector(s);
  const $$ = (s, r=document) => Array.from(r.querySelectorAll(s));
  const text = (el) => (el?.textContent || '').trim();
  const shopRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const pageURL  = (handle) => `${shopRoot.replace(/\/$/, '')}/products/${handle}`;
  const jsonURL  = (handle) => `${shopRoot}products/${handle}.js`;
  const sectionsURL = (handle, sectionId, variantId) => {
    const u = new URL(pageURL(handle), window.location.origin);
    u.searchParams.set('sections', sectionId);
    if (variantId) u.searchParams.set('variant', variantId);
    return u.toString();
  };

  // Remove Dawn animation hooks from a section HTML string
  function stripDawnAnimations(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const root = tpl.content;

    // Remove classes like "scroll-trigger" and any "animate--…"
    root.querySelectorAll('[class]').forEach((el) => {
      try {
        const toRemove = [];
        el.classList.forEach((c) => {
          if (c === 'scroll-trigger' || c.startsWith('animate--') || c === 'appear-ready') {
            toRemove.push(c);
          }
        });
        toRemove.forEach((c) => el.classList.remove(c));
        if (!el.classList.length) el.removeAttribute('class');
      } catch {
        // Fallback for any weird nodes
        const raw = el.getAttribute('class') || '';
        const filtered = raw
          .split(/\s+/)
          .filter(Boolean)
          .filter((c) => c !== 'scroll-trigger' && !c.startsWith('animate--') && c !== 'appear-ready');
        if (filtered.length) el.setAttribute('class', filtered.join(' '));
        else el.removeAttribute('class');
      }
    });

    // Remove common animation data-attrs Dawn uses
    const animAttrs = [
      'data-cascade','data-animate','data-animation','data-motion',
      'data-transition','data-speed','data-delay','data-scroll','data-aos'
    ];
    root.querySelectorAll('*').forEach((el) => {
      for (const a of animAttrs) if (el.hasAttribute(a)) el.removeAttribute(a);
    });

    // Return HTML string of the cleaned fragment
    const container = document.createElement('div');
    container.appendChild(root.cloneNode(true));
    return container.innerHTML;
  }



  // ===== Locate persistent wrapper & section id (so listeners survive swaps) =====
  function getWrapper() {
    const ref = $('form[action*="/cart/add"]') || $('[id^="Slider-Gallery-"]') || $('.product__media-list');
    return ref ? ref.closest('[id^="shopify-section-"]') : null;
  }
  const wrapperEl = getWrapper();
  if (!wrapperEl) return;
  let sectionId = wrapperEl.id.replace(/^shopify-section-/, '') || null;

  // ===== Caches & in-flight guards =====
  const jsonCache    = new Map();             // handle -> product JSON
  const sectionCache = new Map();             // `${handle}|${sectionId}|${variantId||''}` -> HTML
  const pendingJSON  = new Map();             // handle -> Promise
  const pendingSect  = new Map();             // key -> Promise
  let swapSeq = 0;                            // token to keep only the latest swap

  // ===== Fetch helpers (deduped) =====
  async function getJSON(handle) {
    if (jsonCache.has(handle)) return jsonCache.get(handle);
    if (pendingJSON.has(handle)) return pendingJSON.get(handle);
    const p = fetch(jsonURL(handle), { credentials: 'same-origin' })
      .then(r => { if (!r.ok) throw new Error(`JSON ${r.status}`); return r.json(); })
      .then(d => { jsonCache.set(handle, d); pendingJSON.delete(handle); return d; })
      .catch(e => { pendingJSON.delete(handle); throw e; });
    pendingJSON.set(handle, p);
    return p;
  }

  async function getSection(handle, variantId) {
    const key = `${handle}|${sectionId}|${variantId || ''}`;
    if (sectionCache.has(key)) return sectionCache.get(key);
    if (pendingSect.has(key))  return pendingSect.get(key);
    const p = fetch(sectionsURL(handle, sectionId, variantId), { credentials: 'same-origin' })
      .then(r => { if (!r.ok) throw new Error(`Sections ${r.status}`); return r.json(); })
      .then(obj => {
        const html = obj[sectionId] || '';
        sectionCache.set(key, html);
        pendingSect.delete(key);
        return html;
      })
      .catch(e => { pendingSect.delete(key); throw e; });
    pendingSect.set(key, p);
    return p;
  }

  // ===== Read/retain current Size from the LIVE DOM =====
  function currentSizeLabel() {
    const fs =
      wrapperEl.querySelector('.product-form__input--size') ||
      wrapperEl.querySelector('.product-form__input--pill') ||
      wrapperEl.querySelector('fieldset[name*="size" i]') ||
      wrapperEl.querySelector('fieldset [name*="size" i]')?.closest('fieldset') ||
      wrapperEl.querySelector('fieldset');

    if (!fs) return null;
    const r = fs.querySelector('input[type="radio"]:checked');
    if (r) {
      const lab = fs.querySelector(`label[for="${r.id}"]`);
      return (lab && text(lab)) || r.value || r.getAttribute('data-value') || null;
    }
    const sel = fs.querySelector('select[name*="size" i]');
    return sel?.value || null;
  }

  // ===== Decide which variant to show (by retained size → else first available → else first) =====
  function pickVariant(product, wantedSizeLabel) {
    const variants = product?.variants || [];
    const toName = (o)=> (typeof o==='string' ? o : o?.name || '');
    let sizeIdx = -1;
    if (Array.isArray(product?.options)) {
      sizeIdx = product.options.findIndex(o => toName(o).toLowerCase() === 'size');
      if (sizeIdx < 0 && product.options.length >= 2) {
        const names = product.options.map(o => toName(o).toLowerCase());
        const colorIdx = names.indexOf('color');
        sizeIdx = names.findIndex((n,i)=> i !== colorIdx);
      }
    }
    if (wantedSizeLabel && sizeIdx >= 0) {
      const v = variants.find(v => (v[`option${sizeIdx+1}`] || '').toLowerCase() === wantedSizeLabel.toLowerCase());
      if (v) return v;
    }
    return variants.find(v => v.available) || variants[0] || null;
  }

  // ===== Cosmetic: instant hero + color label while we fetch =====
  function quickPreview(inputEl) {
    const preview = inputEl?.getAttribute('data-image');
    if (!preview) return;
    const hero = wrapperEl.querySelector('[id^="Slider-Gallery-"] img, .product__media-list img');
    if (hero) {
      hero.fetchPriority = 'high';
      hero.src = preview.startsWith('http') ? preview : `https:${preview}`;
    }
    const colorEl = wrapperEl.querySelector('.selected-color') || document.querySelector('.selected-color');
    if (colorEl && inputEl?.value) colorEl.textContent = inputEl.value;
    document.querySelectorAll('.ms-swatch-input__label').forEach(l => l.classList.remove('is-active'));
    const lab = document.querySelector(`label[for="${inputEl?.id}"]`);
    lab?.classList.add('is-active');
  }

  // ===== Inject new section HTML (let Dawn re-init) =====
  // Preload & decode the first few images from the incoming section HTML
  async function preloadImagesFromHTML(html, limit = 2) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const imgs = Array.from(tpl.content.querySelectorAll('[id^="Slider-Gallery-"] img, .product__media-list img'))
                      .slice(0, Math.max(1, limit));
    // Create loaders for src URLs (respect protocol-relative)
    const loaders = imgs.map((node) => {
      const src = node.getAttribute('src') || node.getAttribute('data-src') || '';
      if (!src) return null;
      const url = src.startsWith('http') ? src : (src.startsWith('//') ? `https:${src}` : src);
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
      return img.decode ? img.decode().catch(() => {}) : new Promise((res) => { img.onload = res; img.onerror = res; });
    }).filter(Boolean);
    if (loaders.length) await Promise.allSettled(loaders);
  }

  // Inject HTML with: animation scrub, height lock, image pre-decode, and Stamped re-init
  async function inject(html) {
    // 1) Scrub Dawn’s animation hooks so it doesn’t auto-animate
    const clean = stripDawnAnimations(html);

    // 2) Height lock to avoid layout jump during swap
    const rect = wrapperEl.getBoundingClientRect();
    const prevMinH = wrapperEl.style.minHeight;
    wrapperEl.style.minHeight = rect.height ? `${rect.height}px` : prevMinH;

    // 3) Disable CSS animations inside wrapper just for the swap
    wrapperEl.classList.add('no-anim');
    wrapperEl.setAttribute('aria-busy', 'true');

    // 4) Preload & decode hero/first images from the incoming markup
    await preloadImagesFromHTML(clean, 2);

    // 5) Replace content (use replaceChildren to avoid extra parse cost)
    const tplForModal = document.createElement('template');
    tplForModal.innerHTML = clean;
    const tpl = document.createElement('template');
    tpl.innerHTML = clean;
    wrapperEl.replaceChildren(tpl.content); // this preserves the same wrapperEl node (listeners survive)

    // 6) Reflow, then re-enable animations and height
    void wrapperEl.offsetHeight;
    wrapperEl.removeAttribute('aria-busy');
    wrapperEl.classList.remove('no-anim');
    wrapperEl.style.minHeight = prevMinH;

    // 7) Keep section id fresh and reset media scroll
    sectionId = wrapperEl.id.replace(/^shopify-section-/, '') || sectionId;
    wrapperEl.querySelector('[id^="Slider-Gallery-"], .product__media-list')
      ?.scrollTo?.({ left: 0, behavior: 'auto' });

    // 8) Re-initialize Stamped widgets (stars + main UGC) on the fresh DOM
    try {
      if (window.StampedFn) {
        // reload everything (badges & widgets)
        if (typeof window.StampedFn.reloadUGC === 'function') window.StampedFn.reloadUGC();
        if (typeof window.StampedFn.loadBadges === 'function') window.StampedFn.loadBadges();
      }
    } catch (e) {
      console.warn('Stamped reinit failed', e);
    }

    //9 do the modal gallery switch:
    const incomingModal = tplForModal.content.querySelector('product-modal');
    const currentModal  = document.querySelector('product-modal');
    if (incomingModal && currentModal && !wrapperEl.contains(currentModal)) {
      // Replace the existing global modal with the new one
      currentModal.replaceWith(incomingModal);
    }
  }



  // ===== Update URL (no reload) =====
  function updateURL(handle, variantId) {
    const url = new URL(window.location.href);
    url.pathname = `${shopRoot.replace(/\/$/, '')}/products/${handle}`;
    if (variantId) url.searchParams.set('variant', variantId); else url.searchParams.delete('variant');
    history.replaceState({ handle, variantId }, '', url.toString());
  }

  // ===== Prefetch helpers =====
  function prefetchOne(handle, variantId) {
    // warm both caches
    getSection(handle, variantId).catch(()=>{});
    getJSON(handle).catch(()=>{});
    // hint browser to prefetch full page too
    try { const l=document.createElement('link'); l.rel='prefetch'; l.href=pageURL(handle); document.head.appendChild(l); } catch {}
  }
  function prefetchMany(handles) {
    const list = handles.slice(0, PREFETCH_LIMIT);
    let i = 0;
    const workers = Math.min(CONCURRENCY, list.length);
    for (let w=0; w<workers; w++) {
      (function loop(){
        if (i >= list.length) return;
        const h = list[i++]; prefetchOne(h, null);
        setTimeout(loop, 0);
      })();
    }
  }

  // ===== The fast swap (server renders section with the RIGHT variant selected) =====
  async function swap(handle, inputEl) {
    if (!handle) return;
    const myToken = ++swapSeq;

    // 1) quick UI feedback while loading
    quickPreview(inputEl);

    // 2) read retained size, pick target variant from JSON (cache or fetch)
    const wantedSize = currentSizeLabel();
    const product = await getJSON(handle);
    if (myToken !== swapSeq) return; // another swap started

    const variant = pickVariant(product, wantedSize);
    const variantId = variant?.id || null;

    // 3) fetch the section HTML **with variant preselected on the server**
    const html = await getSection(handle, variantId);
    if (myToken !== swapSeq) return;

    // 4) inject & update URL (no extra DOM fiddling needed)
    inject(html);
    updateURL(handle, variantId);
  }

  // ===== Event delegation on the persistent wrapper =====
  // Hover prefetch
  if (PREFETCH_ON_HOVER) {
    wrapperEl.addEventListener('pointerenter', (e) => {
      const input = e.target.closest('.ms-swatch-input__input');
      if (!input || !wrapperEl.contains(input)) return;
      const handle = input.getAttribute('data-handle');
      if (!handle) return;
      // If we already know the current size, prefetch with that variant id too
      const wantedSize = currentSizeLabel();
      if (wantedSize && jsonCache.has(handle)) {
        const v = pickVariant(jsonCache.get(handle), wantedSize);
        prefetchOne(handle, v?.id || null);
      } else {
        // warm generic
        prefetchOne(handle, null);
      }
    }, { passive: true });
  }

  // Change → swap
  wrapperEl.addEventListener('change', (e) => {
    const input = e.target.closest('.ms-swatch-input__input');
    if (!input || !wrapperEl.contains(input)) return;
    const handle = input.getAttribute('data-handle');
    if (!handle) return;
    swap(handle, input).catch(err => console.error('Swatch swap failed', err));
  });

  // ===== Optional: background prefetch (idle / in-view) =====
  const swatches = $$('.ms-swatch-input__input');
  const allHandles = Array.from(new Set(swatches.map(i => i.getAttribute('data-handle')).filter(Boolean)));

  if (PREFETCH_IN_VIEW && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries)=> {
      entries.forEach(async (ent) => {
        if (!ent.isIntersecting) return;
        const h = ent.target.getAttribute('data-handle');
        if (!h) return;
        // warm generic; variant will be chosen on click
        prefetchOne(h, null);
        io.unobserve(ent.target);
      });
    }, { root: null, rootMargin: '200px', threshold: 0 });
    swatches.forEach(i => io.observe(i));
  }

  if (PREFETCH_ON_IDLE && 'requestIdleCallback' in window) {
    requestIdleCallback(() => prefetchMany(allHandles));
  }
})();
